import React, { useState, useMemo, useCallback } from 'react';
import { AssetUrls } from 'config/assets';
import { PublicSetCardAll } from 'store/services/sets/publicSetsApi';
import { SetCardsTableColumn, SetCardsTableSortConfig } from './types';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { rarityColors, formatRarity, parseCollectorNumber } from '../setCardsShared';
import './SetCardsTable.css';

interface SetCardsTableProps {
  cards: PublicSetCardAll[];
  setCode?: string;
}

// Rarity sort order for proper sorting
const raritySortOrder: Record<string, number> = {
  common: 1,
  uncommon: 2,
  rare: 3,
  mythic: 4,
  special: 5,
  bonus: 6,
};

// Format point value - show "???" for null per CLAUDE.md rules
const formatPointValue = (points: number | null): string => {
  if (points === null) return '???';
  return points.toLocaleString();
};

// Thumbnail component with fallback handling
const CardThumbnail: React.FC<{ card: PublicSetCardAll }> = ({ card }) => {
  const getInitialImageUrl = useCallback(() => {
    if (card.imageUris?.small) {
      return AssetUrls.cards.ensureCDNUrl(card.imageUris.small);
    }
    return AssetUrls.cards.getCardImage(card.scryfallID, 'small');
  }, [card.imageUris?.small, card.scryfallID]);

  const [imageSrc, setImageSrc] = useState(getInitialImageUrl);
  const [fallbackAttempt, setFallbackAttempt] = useState(0);

  const handleImageError = useCallback(() => {
    const fallbacks = [
      AssetUrls.cards.getCardImage(card.scryfallID, 'normal'),
      AssetUrls.cards.defaultBack,
    ];

    if (fallbackAttempt < fallbacks.length) {
      setImageSrc(fallbacks[fallbackAttempt]);
      setFallbackAttempt((prev) => prev + 1);
    }
  }, [card.scryfallID, fallbackAttempt]);

  return (
    <img
      src={imageSrc}
      alt={card.name}
      className="set-cards-table-thumbnail"
      loading="lazy"
      onError={handleImageError}
    />
  );
};

const SetCardsTable: React.FC<SetCardsTableProps> = ({ cards, setCode }) => {
  const [sortConfig, setSortConfig] = useState<SetCardsTableSortConfig>({
    column: 'collectorNumber',
    direction: 'asc',
  });

  // Handle column header click for sorting
  const handleSort = (column: SetCardsTableColumn) => {
    if (column === 'thumbnail') return; // Thumbnail is not sortable

    setSortConfig((prev) => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Sort cards based on current sort configuration
  const sortedCards = useMemo(() => {
    const sorted = [...cards].sort((a, b) => {
      const { column, direction } = sortConfig;
      const multiplier = direction === 'asc' ? 1 : -1;

      switch (column) {
        case 'collectorNumber': {
          const aNum = parseCollectorNumber(a.collectorNumber);
          const bNum = parseCollectorNumber(b.collectorNumber);
          if (aNum.numeric !== bNum.numeric) {
            return (aNum.numeric - bNum.numeric) * multiplier;
          }
          return aNum.suffix.localeCompare(bNum.suffix) * multiplier;
        }
        case 'name':
          return a.name.localeCompare(b.name) * multiplier;
        case 'typeLine': {
          const aType = a.typeLine || '';
          const bType = b.typeLine || '';
          return aType.localeCompare(bType) * multiplier;
        }
        case 'rarity': {
          const aRarity = raritySortOrder[a.rarity.toLowerCase()] || 0;
          const bRarity = raritySortOrder[b.rarity.toLowerCase()] || 0;
          return (aRarity - bRarity) * multiplier;
        }
        case 'pointValue': {
          const aVal = a.pointValue ?? -1;
          const bVal = b.pointValue ?? -1;
          return (aVal - bVal) * multiplier;
        }
        default:
          return 0;
      }
    });
    return sorted;
  }, [cards, sortConfig]);

  // Get sort indicator for column headers
  const getSortIndicator = (column: SetCardsTableColumn): string => {
    if (sortConfig.column !== column) return '';
    return sortConfig.direction === 'asc' ? ' \u2191' : ' \u2193';
  };

  // Column header component
  const SortableHeader: React.FC<{
    column: SetCardsTableColumn;
    label: string;
    className?: string;
  }> = ({ column, label, className }) => (
    <th
      className={`set-cards-table-header ${column !== 'thumbnail' ? 'sortable' : ''} ${className || ''}`}
      onClick={() => handleSort(column)}
      role={column !== 'thumbnail' ? 'button' : undefined}
      tabIndex={column !== 'thumbnail' ? 0 : undefined}
      onKeyPress={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && column !== 'thumbnail') {
          e.preventDefault();
          handleSort(column);
        }
      }}
      aria-sort={
        sortConfig.column === column
          ? sortConfig.direction === 'asc'
            ? 'ascending'
            : 'descending'
          : undefined
      }
    >
      {label}
      {getSortIndicator(column)}
    </th>
  );

  return (
    <div className="set-cards-table-wrapper">
      <table className="set-cards-table">
        <thead>
          <tr>
            <SortableHeader column="collectorNumber" label="#" className="col-collector" />
            <SortableHeader column="thumbnail" label="" className="col-thumbnail" />
            <SortableHeader column="name" label="Card Name" className="col-name" />
            <SortableHeader column="typeLine" label="Type" className="col-type" />
            <SortableHeader column="rarity" label="Rarity" className="col-rarity" />
            <SortableHeader column="pointValue" label="Points" className="col-points" />
          </tr>
        </thead>
        <tbody>
          {sortedCards.map((card) => {
            const rarityColor = rarityColors[card.rarity?.toLowerCase()] || rarityColors.common;

            return (
              <tr key={card.scryfallID} className="set-cards-table-row">
                <td className="set-cards-table-cell col-collector">
                  {card.collectorNumber}
                </td>
                <td className="set-cards-table-cell col-thumbnail">
                  <a href={buildCardUrl({ ...card, setCode })} aria-label={`View ${card.name}`}>
                    <CardThumbnail card={card} />
                  </a>
                </td>
                <td className="set-cards-table-cell col-name">
                  <a href={buildCardUrl({ ...card, setCode })} className="set-cards-table-card-link">
                    {card.name}
                  </a>
                </td>
                <td className="set-cards-table-cell col-type">
                  {card.typeLine || '-'}
                </td>
                <td className="set-cards-table-cell col-rarity">
                  <span
                    className="set-cards-table-rarity-badge"
                    style={{ backgroundColor: rarityColor }}
                  >
                    {formatRarity(card.rarity)}
                  </span>
                </td>
                <td className="set-cards-table-cell col-points">
                  {formatPointValue(card.pointValue)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SetCardsTable;
